import React from 'react';
import { SectionCard } from '@/components/common';
import { useAppContext } from '@/hooks/useAppContext';
import { useInvitations } from '@/hooks/useInvitations';
import { InviteForm } from './InviteForm';
import { MemberList } from './MemberList';
import { PendingInvitations } from './PendingInvitations';
import { FaEnvelopeOpenText, FaUserPlus, FaUserShield } from 'react-icons/fa';

interface IslandMembersPanelProps {
  roomId: string;
  /**
   * `admin` is the in-game panel an island admin sees; `superadmin` is the same
   * component inside the platform console. Both talk to the same endpoints —
   * the server decides what each actor may do (§7) — so the mode only tunes the
   * wording and, later, which member actions are offered.
   */
  mode: 'admin' | 'superadmin';
}

/**
 * The shared island membership panel of §8: who holds a role here, the invite
 * form, and the pending invitations — one component, mounted by the in-game
 * `AdminPanel` and by the console's island modal against the same endpoints.
 *
 * The viewer is read from the auth context rather than passed in, because both
 * mount sites would otherwise have to thread the same value through for the one
 * thing it decides: a row for yourself offers no promotion or demotion.
 */
export const IslandMembersPanel: React.FC<IslandMembersPanelProps> = ({ roomId, mode }) => {
  const { auth } = useAppContext();
  const { invitations, isLoading, error, invite, revoke, resend } = useInvitations(roomId);

  return (
    <div className="space-y-6">
      <SectionCard title="Island members" icon={FaUserShield}>
        <MemberList roomId={roomId} mode={mode} currentUserId={auth.user?.id} />
      </SectionCard>

      <SectionCard title="Invite people" icon={FaUserPlus}>
        <p className="text-gray-400 mb-4 leading-relaxed text-sm">
          {mode === 'superadmin'
            ? 'Invitations are sent as you, and the island admins can see them.'
            : 'Everyone who joins this island arrives through an invitation sent to their email.'}
        </p>
        <InviteForm onInvite={invite} />
      </SectionCard>

      <SectionCard title="Pending invitations" icon={FaEnvelopeOpenText}>
        <PendingInvitations
          invitations={invitations}
          isLoading={isLoading}
          error={error}
          onRevoke={revoke}
          onResend={resend}
        />
      </SectionCard>
    </div>
  );
};
